import React, { useEffect, useState } from "react";
// ANT DESIGN COMPONENTS
import { Button, Table, Empty, Tag, Tooltip, Checkbox, Input } from "antd";
// FONT AWESOME LIBRARY AND ICONS
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheckCircle, faSave, faX, faXmark } from "@fortawesome/free-solid-svg-icons";
// I18N TRANSLATION
import { useTranslation } from "react-i18next";
// REDUX
import { useSelector, useDispatch } from "react-redux";
import { updateFormAction, updateLeadingZeroe } from "../../../redux/LeadingZeroes/leadingZeroesSlice";
import {
	useLazyGetLeadingZeroesQuery,
	usePostLeadingZeroesMutation,
	usePutLeadingZeroesMutation,
} from "../../../redux/LeadingZeroes/leadingZeroesAPI";
// COMPONENTS
import TableNoDataBox from "../../components/table/TableNoDataBox";
import LeadingZeroesTableActions from "./LeadingZeroesTableActions";
import GlobalAlert2 from "../../home/GlobalAlert2";
// AUTHORIZATION
import validatePermission from "../../../utils/validatePermissions";
import GETJwtToken from "../../../redux/authentication/authentication";
// UTILS
import { getTableRowClass } from "../../../utils/utils";
// INTERFACES
import { RootState } from "../../../app/store";
import { LeadingZeroeInterface } from "../../../interfaces/LeadingZeroes.interface";

function LeadingZeroesTable() {
	// ************************************************ */
	// GLOBAL VARIABLES ******************************* */
	const dispatch = useDispatch();
	const [t] = useTranslation("global");
	const { Column, ColumnGroup } = Table;
	const BLANK_LEADING_ZEROE: LeadingZeroeInterface = {
		id: "",
		name: "",
		from_device_serial: false,
		refresh_all: false,
		reinitialize_all_devices: false,
		rvu: false,
		set_one_bit: false,
		set_one_byte: false,
	};

	// ************************************************ */
	// USE STATE VARIABLES **************************** */
	const [data, setData] = useState<LeadingZeroeInterface[]>([]);
	const [leadingZeroe, setLeadingZeroe] = useState<LeadingZeroeInterface>(BLANK_LEADING_ZEROE);
	const [errorMessage, setErrorMessage] = useState<any>({});
	const [totalRows, setTotalRows] = useState<number>(0);

	// ************************************************ */
	// SERVICES AND API CALLS ************************* */
	const [
		triggerGetLeadingZeroes,
		{
			data: dataGetLeadingZeroes,
			isLoading: isLoadingGetLeadingZeroes,
			isFetching: isFetchingGetLeadingZeroes,
			isError: isErrorGetLeadingZeroes,
			error: errorGetLeadingZeroes,
		},
	] = useLazyGetLeadingZeroesQuery();

	const [
		triggerPostLeadingZeroe,
		{
			isSuccess: isSuccessPostLeadingZeroe,
			isLoading: isLoadingPostLeadingZeroe,
			isError: isErrorPostLeadingZeroe,
			error: errorPostLeadingZeroe,
			reset: resetPostLeadingZeroe,
		},
	] = usePostLeadingZeroesMutation();

	const [
		triggerPutLeadingZeroe,
		{
			isSuccess: isSuccessPutLeadingZeroe,
			isLoading: isLoadingPutLeadingZeroe,
			isError: isErrorPutLeadingZeroe,
			error: errorPutLeadingZeroe,
			reset: resetPutLeadingZeroe,
		},
	] = usePutLeadingZeroesMutation();

	// ************************************************ */
	// REDUX SLICE VARIABLES ************************** */
	const { theme } = useSelector((state: RootState) => state.home);
	const { permissions } = useSelector((state: RootState) => state.user);
	const {
		refreshTable,
		formAction,
		leadingZeroe: selectedLeadingZeroe,
	} = useSelector((state: RootState) => state.leadingZeroe);

	// ************************************************ */
	// FUNCTIONS ************************************** */
	const getInitialData = async () => {
		const token = await GETJwtToken();
		triggerGetLeadingZeroes({
			token,
		});
	};

	const onClickRefreshTable = async () => {
		getInitialData();
	};

	const onChangeCheckBox = (newValue: boolean, name: string) => {
		const copy: LeadingZeroeInterface = structuredClone(leadingZeroe);
		copy[name] = newValue;
		setLeadingZeroe(copy);
	};

	const onChangeFormValueName = (newValue: string) => {
		const copy: LeadingZeroeInterface = structuredClone(leadingZeroe);
		copy.name = newValue;
		setLeadingZeroe(copy);
	};

	const onClickSave = async () => {
		const token = await GETJwtToken();
		if (formAction === "CREATE") {
			const BODY = {
				token,
				body: leadingZeroe,
			};
			triggerPostLeadingZeroe(BODY);
		} else {
			const BODY = {
				token,
				id: leadingZeroe.id,
				body: leadingZeroe,
			};
			triggerPutLeadingZeroe(BODY);
		}
	};

	const resetForm = () => {
		resetPostLeadingZeroe();
		resetPutLeadingZeroe();
		setLeadingZeroe(BLANK_LEADING_ZEROE);
		setErrorMessage({});
		dispatch(updateFormAction("CREATE"));
		dispatch(updateLeadingZeroe(BLANK_LEADING_ZEROE));
	};

	const checkFormIsValid = () => {
		let formIsValid = true;
		if (leadingZeroe.name === "") {
			formIsValid = false;
		}
		return formIsValid;
	};

	// ************************************************* */
	// USE EFFECT ************************************** */
	useEffect(() => {
		getInitialData();
	}, []);

	useEffect(() => {
		if (refreshTable) {
			onClickRefreshTable();
		}
	}, [refreshTable]);

	useEffect(() => {
		if (dataGetLeadingZeroes && dataGetLeadingZeroes.data) {
			setData(dataGetLeadingZeroes.data);
			setTotalRows(dataGetLeadingZeroes.total);
		}
	}, [dataGetLeadingZeroes]);

	useEffect(() => {
		if (errorGetLeadingZeroes) {
			setErrorMessage(errorGetLeadingZeroes);
			setData([]);
		}
	}, [errorGetLeadingZeroes]);

	useEffect(() => {
		resetForm();
	}, [isSuccessPostLeadingZeroe, isSuccessPutLeadingZeroe, isErrorPostLeadingZeroe, isErrorPutLeadingZeroe]);

	useEffect(() => {
		if (formAction === "CREATE") {
			setLeadingZeroe(BLANK_LEADING_ZEROE);
		}
	}, [formAction]);

	useEffect(() => {
		setLeadingZeroe(selectedLeadingZeroe);
	}, [selectedLeadingZeroe]);

	// ************************************************ */
	// COMPONENT ************************************** */
	return (
		<div>
			<div>
				{validatePermission("leading-zeroes-view", permissions) ? (
					<Table
						locale={{
							emptyText: (
								<TableNoDataBox
									isError={isErrorGetLeadingZeroes}
									errorMessage={
										errorMessage && errorMessage.data && errorMessage.data.message ? errorMessage.data.message : "Error"
									}
									noItemsFound='No data found'
								/>
							),
						}}
						rowClassName={(record, index) => getTableRowClass(index, theme)}
						dataSource={data}
						pagination={{
							defaultPageSize: 10,
							showSizeChanger: true,
							pageSizeOptions: ["10", "25", "50", "100"],
						}}
						size='small'
						className='mt-3'
						loading={
							isFetchingGetLeadingZeroes ||
							isLoadingGetLeadingZeroes ||
							isLoadingPostLeadingZeroe ||
							isLoadingPutLeadingZeroe
						}
						footer={() => (
							<div className='generalStyles__flexEnd'>
								<Tag>{totalRows} Rows</Tag>
							</div>
						)}
					>
						{/** NAME */}
						<ColumnGroup title='Name'>
							<Column
								width={200}
								title={
									<Input
										size='small'
										placeholder='Name'
										value={formAction === "CREATE" ? leadingZeroe.name : ""}
										disabled={formAction === "EDIT"}
										onChange={(event: React.FormEvent<HTMLInputElement>) => {
											onChangeFormValueName(event.currentTarget.value);
										}}
									/>
								}
								dataIndex='name'
								key='name'
								render={(text, record: LeadingZeroeInterface) => (
									<div>
										{formAction === "EDIT" && record.id === leadingZeroe.id ? (
											<Input
												size='small'
												placeholder='Name'
												value={leadingZeroe.name}
												onChange={(event: React.FormEvent<HTMLInputElement>) => {
													onChangeFormValueName(event.currentTarget.value);
												}}
											/>
										) : (
											<span>{text}</span>
										)}
									</div>
								)}
							/>
						</ColumnGroup>
						{/** FROM DEVICE SERIAL */}
						<ColumnGroup title='From Device Serial'>
							<Column
								width={200}
								title={
									<Checkbox
										disabled={formAction === "EDIT"}
										checked={formAction === "CREATE" ? leadingZeroe.from_device_serial : false}
										onChange={(e) => {
											onChangeCheckBox(e.target.checked, "from_device_serial");
										}}
									/>
								}
								dataIndex='from_device_serial'
								key='from_device_serial'
								render={(text, record: LeadingZeroeInterface) => (
									<div>
										{formAction === "EDIT" && record.id === leadingZeroe.id ? (
											<Checkbox
												disabled={formAction === "EDIT"}
												checked={leadingZeroe.from_device_serial}
												onChange={(e) => {
													onChangeCheckBox(e.target.checked, "from_device_serial");
												}}
											/>
										) : (
											<>
												{text ? (
													<FontAwesomeIcon className='generalStyles__success' icon={faCheckCircle} />
												) : (
													<FontAwesomeIcon className='generalStyles__error' icon={faXmark} />
												)}
											</>
										)}
									</div>
								)}
							/>
						</ColumnGroup>
						{/** REFRESH ALL */}
						<ColumnGroup title='Refresh All'>
							<Column
								width={200}
								title={
									<Checkbox
										disabled={formAction === "EDIT"}
										checked={formAction === "CREATE" ? leadingZeroe.refresh_all : false}
										onChange={(e) => {
											onChangeCheckBox(e.target.checked, "refresh_all");
										}}
									/>
								}
								dataIndex='refresh_all'
								key='refresh_all'
								render={(text, record: LeadingZeroeInterface) => (
									<div>
										{formAction === "EDIT" && record.id === leadingZeroe.id ? (
											<Checkbox
												disabled={formAction === "EDIT"}
												checked={leadingZeroe.refresh_all}
												onChange={(e) => {
													onChangeCheckBox(e.target.checked, "refresh_all");
												}}
											/>
										) : (
											<>
												{text ? (
													<FontAwesomeIcon className='generalStyles__success' icon={faCheckCircle} />
												) : (
													<FontAwesomeIcon className='generalStyles__error' icon={faXmark} />
												)}
											</>
										)}
									</div>
								)}
							/>
						</ColumnGroup>
						{/** REINITIALIZE ALL DEVICES */}
						<ColumnGroup title='Reinitialize All Devices'>
							<Column
								width={200}
								title={
									<Checkbox
										disabled={formAction === "EDIT"}
										checked={formAction === "CREATE" ? leadingZeroe.reinitialize_all_devices : false}
										onChange={(e) => {
											onChangeCheckBox(e.target.checked, "reinitialize_all_devices");
										}}
									/>
								}
								dataIndex='reinitialize_all_devices'
								key='reinitialize_all_devices'
								render={(text, record: LeadingZeroeInterface) => (
									<div>
										{formAction === "EDIT" && record.id === leadingZeroe.id ? (
											<Checkbox
												disabled={formAction === "EDIT"}
												checked={leadingZeroe.reinitialize_all_devices}
												onChange={(e) => {
													onChangeCheckBox(e.target.checked, "reinitialize_all_devices");
												}}
											/>
										) : (
											<>
												{text ? (
													<FontAwesomeIcon className='generalStyles__success' icon={faCheckCircle} />
												) : (
													<FontAwesomeIcon className='generalStyles__error' icon={faXmark} />
												)}
											</>
										)}
									</div>
								)}
							/>
						</ColumnGroup>
						{/** RVU */}
						<ColumnGroup title='RVU'>
							<Column
								width={200}
								title={
									<Checkbox
										disabled={formAction === "EDIT"}
										checked={formAction === "CREATE" ? leadingZeroe.rvu : false}
										onChange={(e) => {
											onChangeCheckBox(e.target.checked, "rvu");
										}}
									/>
								}
								dataIndex='rvu'
								key='rvu'
								render={(text, record: LeadingZeroeInterface) => (
									<div>
										{formAction === "EDIT" && record.id === leadingZeroe.id ? (
											<Checkbox
												disabled={formAction === "EDIT"}
												checked={leadingZeroe.rvu}
												onChange={(e) => {
													onChangeCheckBox(e.target.checked, "rvu");
												}}
											/>
										) : (
											<>
												{text ? (
													<FontAwesomeIcon className='generalStyles__success' icon={faCheckCircle} />
												) : (
													<FontAwesomeIcon className='generalStyles__error' icon={faXmark} />
												)}
											</>
										)}
									</div>
								)}
							/>
						</ColumnGroup>
						{/** SET ONE BIT */}
						<ColumnGroup title='Set One Bit'>
							<Column
								width={200}
								title={
									<Checkbox
										disabled={formAction === "EDIT"}
										checked={formAction === "CREATE" ? leadingZeroe.set_one_bit : false}
										onChange={(e) => {
											onChangeCheckBox(e.target.checked, "set_one_bit");
										}}
									/>
								}
								dataIndex='set_one_bit'
								key='set_one_bit'
								render={(text, record: LeadingZeroeInterface) => (
									<div>
										{formAction === "EDIT" && record.id === leadingZeroe.id ? (
											<Checkbox
												disabled={formAction === "EDIT"}
												checked={leadingZeroe.set_one_bit}
												onChange={(e) => {
													onChangeCheckBox(e.target.checked, "set_one_bit");
												}}
											/>
										) : (
											<>
												{text ? (
													<FontAwesomeIcon className='generalStyles__success' icon={faCheckCircle} />
												) : (
													<FontAwesomeIcon className='generalStyles__error' icon={faXmark} />
												)}
											</>
										)}
									</div>
								)}
							/>
						</ColumnGroup>
						{/** SET ONE BYTE */}
						<ColumnGroup title='Set One Byte'>
							<Column
								width={200}
								title={
									<Checkbox
										disabled={formAction === "EDIT"}
										checked={formAction === "CREATE" ? leadingZeroe.set_one_byte : false}
										onChange={(e) => {
											onChangeCheckBox(e.target.checked, "set_one_byte");
										}}
									/>
								}
								dataIndex='set_one_byte'
								key='set_one_byte'
								render={(text, record: LeadingZeroeInterface) => (
									<div>
										{formAction === "EDIT" && record.id === leadingZeroe.id ? (
											<Checkbox
												disabled={formAction === "EDIT"}
												checked={leadingZeroe.set_one_byte}
												onChange={(e) => {
													onChangeCheckBox(e.target.checked, "set_one_byte");
												}}
											/>
										) : (
											<>
												{text ? (
													<FontAwesomeIcon className='generalStyles__success' icon={faCheckCircle} />
												) : (
													<FontAwesomeIcon className='generalStyles__error' icon={faXmark} />
												)}
											</>
										)}
									</div>
								)}
							/>
						</ColumnGroup>
						{/** ACTIONS */}
						<ColumnGroup align='center' title={t("edgeManagement.banks.actions")} width={200}>
							<Column
								align='center'
								width={200}
								title={
									<div className='generalStyles__flexCenter'>
										<div>
											<Tooltip title={t("general.save")}>
												<Button
													className={formAction === "CREATE" && checkFormIsValid() ? "buttonStyle__10" : ""}
													disabled={!checkFormIsValid() || formAction === "EDIT"}
													onClick={onClickSave}
													icon={<FontAwesomeIcon icon={faSave} />}
												/>
											</Tooltip>
										</div>
										<div className='generalStyles__mlFix'>
											<Tooltip title={t("general.clear")}>
												<Button
													className={formAction === "CREATE" ? "buttonStyle__14" : ""}
													onClick={resetForm}
													disabled={formAction === "EDIT"}
													icon={<FontAwesomeIcon icon={faX} />}
												/>
											</Tooltip>
										</div>
									</div>
								}
								key='action'
								render={(_: any, record: LeadingZeroeInterface) => (
									<div>
										{formAction === "EDIT" && record.id === leadingZeroe.id ? (
											<div className='generalStyles__flexCenter'>
												<div>
													<Tooltip title={t("general.save")}>
														<Button
															className='buttonStyle__10'
															disabled={!checkFormIsValid()}
															onClick={onClickSave}
															icon={<FontAwesomeIcon icon={faSave} />}
														/>
													</Tooltip>
												</div>
												<div className='generalStyles__mlFix'>
													<Tooltip title={t("general.cancel")}>
														<Button
															className='buttonStyle__14'
															onClick={resetForm}
															icon={<FontAwesomeIcon icon={faX} />}
														/>
													</Tooltip>
												</div>
											</div>
										) : (
											<LeadingZeroesTableActions selectedItem={record} />
										)}
									</div>
								)}
							/>
						</ColumnGroup>
					</Table>
				) : (
					<>
						<div className='generalStyles__noAccessToListTable'>
							<Empty
								description={t("organizationManagement.listNotAvailableOrganizations")}
								image={Empty.PRESENTED_IMAGE_SIMPLE}
							/>
						</div>
					</>
				)}
			</div>
			{/** ---------------------------------------------------- */}
			{/** GLOBAL ALERT */}
			<GlobalAlert2
				isError={isErrorPostLeadingZeroe}
				isSuccess={isSuccessPostLeadingZeroe}
				requestType='POST'
				error={errorPostLeadingZeroe}
				name='Leading Zeroe'
			/>
			<GlobalAlert2
				isError={isErrorPutLeadingZeroe}
				isSuccess={isSuccessPutLeadingZeroe}
				requestType='PUT'
				error={errorPutLeadingZeroe}
				name='Leading Zeroe'
			/>
		</div>
	);
}

export default LeadingZeroesTable;
